import { usePlatformLocale } from "../lib/platform-language";
import { translateUi } from "../lib/i18n.ts";
import { useEffect, useRef, useState } from "react";
import { Calendar } from "lucide-react";

// Input-i native "type=date" e shfaq daten sipas locale-s se BROWSER-it (shpesh mm/dd/yyyy ne
// Chrome anglisht), jo sipas <html lang="sq"> te faqes - keshtu qe s'mund te detyrohet ne
// dd/mm/yyyy pa e zevendesuar. Ky komponent shfaq gjithmone tekst dd/mm/yyyy (shtypje me
// vetë-vendosje "/"), dhe mban nje input te fshehur "type=date" vetem per kalendarin klikues.
// Jashte komponentit, vlera mbetet gjithmone ISO yyyy-mm-dd - i njejti kontratë si input-i i
// vjeter, per pajtueshmeri me krejt kodin ekzistues (state, PUT/POST body-t, etj).

function isoToDisplay(iso: string | undefined): string {
  if (!iso) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
}

function displayToIso(display: string): string | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(display.trim());
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (month < 1 || month > 12) return null;
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) return null;
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function formatTyped(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length > 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}

export function DateInput({
  value, defaultValue, onChange, onBlur, min, max, disabled, className, id, name, placeholder,
  monthOnly = false,
}: {
  value?: string;
  defaultValue?: string;
  onChange?: (iso: string) => void;
  onBlur?: (iso: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  placeholder?: string;
  monthOnly?: boolean;
}) {
  usePlatformLocale();
  const isControlled = value !== undefined;
  const [text, setText] = useState(() => isoToDisplay(value ?? defaultValue));
  const [lastValidIso, setLastValidIso] = useState(() => value ?? defaultValue ?? "");
  const pickerRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isControlled) setText(isoToDisplay(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // CRM's payment month control predates the explicit prop and is identified by
  // its unique legacy placeholder until that call site is converted.
  if (monthOnly || placeholder === "Zgjedh datën") {
    const monthValue = (value ?? defaultValue ?? "").slice(0, 7);
    return (
      <span className={`date-input-wrap date-input-month${className ? ` ${className}` : ""}`}>
        <input
          type="month"
          id={id}
          name={name}
          disabled={disabled}
          value={monthValue}
          min={min?.slice(0, 7)}
          max={max?.slice(0, 7)}
          onChange={(event) => {
            const iso = event.target.value ? `${event.target.value}-01` : "";
            onChange?.(iso);
            onBlur?.(iso);
          }}
        />
      </span>
    );
  }

  function handleTextChange(raw: string) {
    const formatted = formatTyped(raw);
    setText(formatted);
    const iso = displayToIso(formatted);
    if (iso) {
      setLastValidIso(iso);
      onChange?.(iso);
    }
  }

  function handleTextBlur() {
    const iso = displayToIso(text);
    if (iso) {
      onBlur?.(iso);
    } else {
      // Tekst i paplote/i pavlefshem kur del nga fusha - kthehet ne vleren e fundit te vlefshme
      // (jo bosh, jo mbeturine) qe fusha te mos mbetet ne nje gjendje konfuze per perdoruesin.
      setText(isoToDisplay(isControlled ? value : lastValidIso));
    }
  }

  function handlePickerChange(iso: string) {
    setText(isoToDisplay(iso));
    setLastValidIso(iso);
    onChange?.(iso);
    onBlur?.(iso);
  }

  return (
    <span className={`date-input-wrap${className ? ` ${className}` : ""}`}>
      <input
        type="text"
        inputMode="numeric"
        placeholder={placeholder || "dd/mm/yyyy"}
        id={id}
        name={name}
        disabled={disabled}
        value={text}
        onChange={(event) => handleTextChange(event.target.value)}
        onBlur={handleTextBlur}
      />
      <button
        type="button"
        className="date-input-pick-btn"
        disabled={disabled}
        aria-label={translateUi("Hap kalendarin", "Open calendar")}
        onClick={() => {
          const picker = pickerRef.current;
          if (!picker) return;
          if (typeof picker.showPicker === "function") picker.showPicker();
          else picker.click();
        }}
      >
        <Calendar size={14} />
      </button>
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        className="date-input-native-picker"
        value={isControlled ? (value || "") : lastValidIso}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(event) => handlePickerChange(event.target.value)}
      />
    </span>
  );
}
